import { Button } from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import styles from "./InputCpfCnpj.module.scss";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { validateCPF, validateCNPJ, cnpjMask, cpfMask } from "utils/utils";
import { Keyboard } from "components/keyboard/Keyboard";
import { useTranslation } from "i18n/useTranslation";
import { Trans } from "react-i18next";

type DocumentType = "cpf" | "cnpj";

const CPF_MAX_LENGTH = 11;
const CNPJ_MAX_LENGTH = 14;

interface InputCpfCnpjProps {
  onPressContinuar: (cpfOrCnpj?: string) => void;
  value?: string;
  required?: boolean;
  /** Oculta o título quando o texto é exibido pelo layout pai (ex.: CpfStepLayout). */
  hideTitle?: boolean;
  /** Se informado, o segundo botão exibe "Cancelar" e chama este callback (ex.: voltar para leitura). Caso contrário, exibe "Não informar" e continua sem CPF. */
  onPressCancel?: () => void;
}

export const InputCpfCnpj: FC<InputCpfCnpjProps> = ({
  onPressContinuar,
  value,
  required,
  hideTitle = false,
  onPressCancel,
}) => {
  const { resolution, orientation } = useScreenStore();
  const { t } = useTranslation();
  const [cpfOrCnpj, setCpfOrCnpj] = useState(value ?? "");
  const [disableContinuar, setDisableContinuar] = useState(true);
  const [erro, setErro] = useState("");
  const [docType, setDocType] = useState<DocumentType>("cpf");
  const maxLength = docType === "cpf" ? CPF_MAX_LENGTH : CNPJ_MAX_LENGTH;

  useEffect(() => {
    setCpfOrCnpj("");
    setErro("");
    setDisableContinuar(true);
  }, [docType]);

  useEffect(() => {
    if (docType === "cpf" && cpfOrCnpj.length === CPF_MAX_LENGTH) {
      if (validateCPF(cpfOrCnpj)) {
        setErro("");
        setDisableContinuar(false);
      } else {
        setErro(t("input_cpf_cnpj.error.cpf_invalid"));
        setDisableContinuar(true);
      }
    } else if (docType === "cnpj" && cpfOrCnpj.length === CNPJ_MAX_LENGTH) {
      if (validateCNPJ(cpfOrCnpj)) {
        setErro("");
        setDisableContinuar(false);
      } else {
        setErro(t("input_cpf_cnpj.error.cnpj_invalid"));
        setDisableContinuar(true);
      }
    } else {
      setErro("");
      setDisableContinuar(true);
    }
  }, [cpfOrCnpj, docType, t]);

  const onPressKeyboard = (key: string) => {
    if (cpfOrCnpj.length < maxLength) {
      setCpfOrCnpj((prev) => prev + key);
    }
  };

  const onPressBack = () => {
    setCpfOrCnpj((prev) => prev.slice(0, -1));
  };

  const onPressClear = () => {
    setCpfOrCnpj("");
  };

  const displayValue =
    docType === "cnpj" ? cnpjMask(cpfOrCnpj) : cpfMask(cpfOrCnpj);

  const onClickNaoInformar = () => {
    onPressContinuar("");
  };

  const onClickCancelar = () => {
    onPressCancel?.();
  };

  const onClickContinuar = () => {
    if (!disableContinuar) {
      onPressContinuar(cpfOrCnpj);
    }
  };

  return (
    <div id={styles.InputCpfCnpj}>
      {!hideTitle && (
        <span
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
          }}
        >
          <Trans i18nKey="input_cpf_cnpj.title">
            Informe seu{" "}
            <b
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
              }}
            >
              CPF
            </b>
            , para ter uma experiência{" "}
            <b
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
              }}
            >
              ainda mais incrível!
            </b>
          </Trans>
        </span>
      )}

      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tab} ${docType === "cpf" ? styles.tabActive : ""}`}
          onClick={() => setDocType("cpf")}
        >
          CPF
        </button>
        <button
          className={`${styles.tab} ${docType === "cnpj" ? styles.tabActive : ""}`}
          onClick={() => setDocType("cnpj")}
        >
          CNPJ
        </button>
      </div>

      <div className={styles.inputContainer}>
        <span
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 40 : 28,
            padding: resolution === ResolutionType.FULL_HD ? 26 : 20,
          }}
        >
          {cpfOrCnpj.length === 0
            ? docType === "cnpj"
              ? "CNPJ"
              : t("input_cpf_cnpj.placeholder")
            : ""}
          {displayValue}
        </span>
        <span>{erro}</span>
      </div>

      <div className={styles.keyboard}>
        <Keyboard
          type={docType === "cpf" ? "clear" : "text"}
          onPressBack={onPressBack}
          onKeyPress={onPressKeyboard}
          onPressClear={onPressClear}
          hideClear
        />
      </div>

      <div
        className={styles.buttonsContainer}
        style={{
          flexDirection:
            orientation === OrientationType.LANDSCAPE ? "row" : "column",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={onClickContinuar}
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 45 : 32,
            marginBottom: orientation === OrientationType.LANDSCAPE ? 0 : 16,
            maxWidth:
              orientation === OrientationType.LANDSCAPE ? "48%" : undefined,
          }}
        >
          {t("input_cpf_cnpj.buttons.confirm")}
        </Button>

        {(!required || onPressCancel) && (
          <Button
            className={styles.skip}
            fullWidth
            variant="text"
            onClick={onPressCancel != null ? onClickCancelar : onClickNaoInformar}
            style={{
              fontSize: resolution === ResolutionType.FULL_HD ? 45 : 32,
              maxWidth:
                orientation === OrientationType.LANDSCAPE ? "48%" : undefined,
            }}
          >
            {t(onPressCancel != null ? "input_cpf_cnpj.buttons.cancel" : "input_cpf_cnpj.buttons.skip")}
          </Button>
        )}
      </div>
    </div>
  );
};
